import { useEffect, useState } from 'react'
import { useMyPlatforms } from '@/hooks/queries'
import { connectPlatform } from '@/lib/oauth'
import { useConnectStore } from '@/store/connect'
import { platformName } from '@/lib/text'
import type { PlatformConnection } from '@/models/external-platform'
import { SkeletonLine } from './ui/Skeleton'

/**
 * Linked third-party accounts, with a way to connect or reconnect each one.
 * Before this, Discord could only be linked by signing in with it.
 */
export function ConnectedAccounts() {
  const { data: platforms, isLoading, isError } = useMyPlatforms()
  const [leaving, setLeaving] = useState<string | null>(null)
  // Set while the platform's sign-in is open in another tab.
  const waiting = useConnectStore((state) => state.pending)
  const cancelWaiting = useConnectStore((state) => state.cancel)

  // Closing that tab without finishing would leave this waiting forever. Once
  // the user is back on this page, give the connection a moment to arrive and
  // then go back to "Connect".
  useEffect(() => {
    if (waiting === null) return
    let timer: number | undefined
    const onFocus = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(cancelWaiting, 15_000)
    }
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      window.clearTimeout(timer)
    }
  }, [waiting, cancelWaiting])

  const connect = (platform: PlatformConnection) => {
    const name = platform.name
    if (!name) return
    void connectPlatform(
      { name, auth_url: platform.auth_url },
      { beforeLeave: () => setLeaving(name) },
    ).finally(() => setLeaving(null))
  }

  return (
    <section className="connected-accounts" aria-labelledby="connected-accounts-title">
      <div className="connected-accounts-title" id="connected-accounts-title">
        Connected accounts
      </div>

      {isLoading ? <ConnectedAccountRowSkeleton /> : null}
      {isError ? (
        <div className="connected-accounts-empty">Could not load your connected accounts.</div>
      ) : null}
      {platforms && platforms.length === 0 ? (
        <div className="connected-accounts-empty">No platforms are available to connect.</div>
      ) : null}

      {platforms?.map((platform) => {
        const name = platformName(platform.name)
        const expired =
          platform.connected && platform.expires_at !== null && platform.expires_at < new Date()
        const busy = leaving === platform.name
        const isWaiting = waiting !== null && waiting === platform.name?.toLowerCase()
        const status = !platform.connected ? 'Not connected' : expired ? 'Expired' : 'Connected'

        return (
          <div className="connected-account" key={platform.id ?? platform.name}>
            <img
              className="connected-account-logo"
              src={`/icons/${platform.name?.toLowerCase()}.png`}
              onError={(event) => {
                event.currentTarget.src = '/icons/logo.png'
              }}
              alt=""
            />
            <div className="connected-account-text">
              <div className="connected-account-name">{name}</div>
              <div
                className={
                  platform.connected && !expired
                    ? 'connected-account-status connected'
                    : 'connected-account-status'
                }
              >
                {status}
              </div>
            </div>
            <button
              type="button"
              className="connected-account-button"
              onClick={() => connect(platform)}
              disabled={leaving !== null}
              aria-label={`${platform.connected ? 'Reconnect' : 'Connect'} ${name}`}
              title={
                isWaiting
                  ? `Finish connecting ${name} in the new tab, or click to open it again`
                  : undefined
              }
            >
              {busy || isWaiting ? <span className="spinner-inline" aria-hidden="true" /> : null}
              {busy
                ? 'Redirecting…'
                : isWaiting
                  ? 'Waiting…'
                  : platform.connected
                    ? 'Reconnect'
                    : 'Connect'}
            </button>
          </div>
        )
      })}
    </section>
  )
}

/** One platform row's own markup while loading; the button keeps its size. */
function ConnectedAccountRowSkeleton() {
  return (
    <div className="connected-account is-skeleton" aria-hidden="true">
      <div className="connected-account-logo skeleton" style={{ borderRadius: '100%' }} />
      <div className="connected-account-text">
        <div className="connected-account-name">
          <SkeletonLine width="5em" />
        </div>
        <div className="connected-account-status">
          <SkeletonLine width="7em" />
        </div>
      </div>
      <span className="connected-account-button skeleton skeleton-ghost">Connect</span>
    </div>
  )
}

/** The whole section while the profile itself is still loading. */
export function ConnectedAccountsSkeleton() {
  return (
    <section className="connected-accounts is-skeleton" aria-hidden="true">
      <div className="connected-accounts-title">Connected accounts</div>
      <ConnectedAccountRowSkeleton />
    </section>
  )
}
